/* eslint-disable max-len */
import { generateClient } from 'aws-amplify/api';
import { getAuthToken } from '../aws-config';
import * as query from '../graphql/queries';
import * as mutation from '../graphql/mutations';
import * as subscription from '../graphql/subscriptions';
import logger from './logger';

// AWS Amplify v6 with Auth0 OIDC - client generated on-demand per operation
// This ensures Amplify configuration is applied when the client is created

const OPERATION_FILE_NAMES = {
  query: 'queries.js',
  mutation: 'mutations.js',
  subscription: 'subscriptions.js',
};

const APIWrapper = {
  /**
   * The graphQL operations that our API allows
   */
  operations: {
    query,
    mutation,
    subscription,
  },
  /**
   * queryApi - Call the GraphQL API from the client and run a query
   * @param {Object} operation
   * @param {'query' | 'mutation'} opration.opType The graphql operation to perform
   * @param {string} operation.opName The name of the operation to perform
   * @param {boolean} setShouldLogOut flag if user is authorized to perform this operation or not
   * @param {Object} [queryVars] Parameters for the query variables
   * @returns
   */
  queryApi(operation, setShouldLogOut, queryVars) {
    async function callGraphQL(operationToCall, apiQueryVars) {
      const [opType, opName] = Object.entries(operationToCall)[0];

      // Check that the operation type is allowed
      if (!Object.keys(APIWrapper.operations).includes(opType)) {
        throw new Error(
          `${opType} is not a valid value for this API operation. Please use either 'query' or 'mutation'.`,
        );
      }

      // Check that the operation name actually exists for that type (catches
      // typos and query/mutation mix-ups before they hit the network).
      if (!APIWrapper.operations[opType][opName]) {
        throw new Error(
          `${opName} is not defined as a ${opType} in graphql/${OPERATION_FILE_NAMES[opType]}.`,
        );
      }

      let data;
      let errors;
      let items;
      let nextToken;
      let aggregateItems;
      let total;
      let filteredRes;
      const results = [];
      const errorMessages = [];

      try {
        const authToken = await getAuthToken();
        if (!authToken) {
          setShouldLogOut(true);
          throw new Error('No auth token available');
        }

        ({ data, errors } = await generateClient().graphql({
          query: APIWrapper.operations[opType][opName],
          variables: {
            ...apiQueryVars,
            ...(nextToken && { nextToken }),
          },
          authMode: 'openidConnect',
          authToken,
        }));
      } catch (e) {
        if (e.error === 'missing_refresh_token' || e.error === 'invalid_grant') {
          // refresh token has expired, let's prompt user to log back in
          setShouldLogOut(true);
        }
        ({ data, errors } = e);
        // TODO: Figure out how we want to handle these
        // non-breaking errors
        if (errors && Object.keys(errors).length) {
          Object.keys(errors).forEach((err) => {
            const { message } = errors[err];
            errorMessages.push(message);
          });
        } else {
          // e wasn't a GraphQLResult-shaped error (e.g. a thrown network,
          // config, or validation error) - without this, its message is
          // silently dropped since data/errors both come back undefined.
          errorMessages.push(e.message || String(e));
        }
        logger.error(`${opName} failed: ${errorMessages.join('; ')}`);
      }
      if (data) {
        if (errorMessages.includes('Request failed with status code 401')) {
          // user authentication failed with appsync, log user out
          setShouldLogOut(true);
        } else if (data[opName] !== null && data[opName].items) {
          ({
            [opName]: {
              items, nextToken, total, aggregateItems,
            },
          } = data);
          results.push(items);

          filteredRes = results[0].filter((item) => item !== null);
        } else {
          // for operations where results are a single record
          results.push(data[opName]);
          filteredRes = results.filter((item) => item !== null);
        }
      } else if (errorMessages.length) {
        filteredRes = [];
      } else {
        filteredRes = [];
        logger.error(
          "No data returned from API - this could be because a search operation could not be completed b/c open search didn't have any data backfilled",
        );
      }

      return {
        data: [filteredRes],
        errors,
        nextToken,
        total,
        aggregateItems,
      };
    }
    return callGraphQL(operation, queryVars);
  },

  async subscribeApi(operation, setShouldLogOut, subscriptionVars) {
    /**
     * subscribeApi - Call the GraphQL API from the client and run a query
     * @param {Object} operation
     * @param {'subscription'} opration.opType The graphql operation to perform
     * @param {string} operation.opName The name of the operation to perform
     * @param {boolean} setShouldLogOut flag if user is authorized to perform this operation or not
     * @param {Object} subscriptionVars the subscription variables
     * @returns
     */
    const [opType, opName] = Object.entries(operation)[0];
    // Check that the operation type is allowed
    if (opType !== 'subscription') {
      throw new Error(
        `${opType} is not a valid value for this API operation. Please use 'subscription'.`,
      );
    }

    try {
      const authToken = await getAuthToken();
      if (!authToken) {
        setShouldLogOut(true);
        throw new Error('No auth token available');
      }

      // For aws-amplify v6 subscriptions with OIDC, create a client with custom headers
      const client = generateClient({
        authMode: 'none',
        headers: async () => ({ Authorization: authToken }),
      });

      return client.graphql({
        query: APIWrapper.operations[opType][opName],
        variables: { ...subscriptionVars },
      });
    } catch (e) {
      if (e.error === 'missing_refresh_token' || e.error === 'invalid_grant') {
        setShouldLogOut(true);
      }
      logger.error('Subscription exception:', e);
      throw e;
    }
  },
  /**
   * An additional helper function to format filters used in GraphQL API queries.
   * @param {[Object]} a list of objs that follow the format: {field: {operation: value}}
   * Returns a the filters in the following nested format that can be used in an api call:
   * {
   *  field1: {
   *    operation: value,
   *    and: {
   *      field2: {
   *        operation: value,
   *        and: {
   *          ...
   *        }
   *      }
   *    }
   *  }
   * }
   * or false if something goes wrong
   */
  formatFilters(filterList) {
    let formattedFilters = {};
    let error = false;
    filterList.forEach((filter) => {
      // should only have one key (field)
      if (Object.keys(filter).length === 1) {
        try {
          const field = Object.keys(filter)[0];
          const operationObj = filter[field];
          const op = Object.keys(operationObj)[0];
          const value = filter[field][op];

          let newFilters = {};
          if (Object.keys(formattedFilters).length > 0) {
            newFilters = {
              and: formattedFilters,
            };
          }
          newFilters[field] = {};
          newFilters[field][op] = value;

          formattedFilters = newFilters;
        } catch (err) {
          logger.error(err);
          logger.debug("Couldn't format filters");
          error = true;
        }
      } else {
        error = true;
      }
    });
    if (error) {
      return false;
    }
    return formattedFilters;
  },
};

export default APIWrapper;
